import { Leg, PairPosition } from "./../types";
import { calculateProfit } from "./calculateProfits";

function createPairPosition(buy?: Leg, sell?: Leg): PairPosition {
  let maxProfit = 0;
  let maxLoss = 0;
  if (buy && sell) {
    const netPremium = buy.price - sell.price;
    const strikeDifference = Math.abs(buy.strikePrice - sell.strikePrice);
    const profit =
      netPremium > 0 ? strikeDifference - netPremium : Math.abs(netPremium);
    maxLoss = (strikeDifference - profit) * buy.quantity;
    maxProfit = profit * buy.quantity;
  } else if (buy) {
    maxProfit = Infinity;
    maxLoss = buy.price * buy.quantity;
  } else if (sell) {
    maxProfit = sell.price * sell.quantity;
    maxLoss = Infinity;
  }
  return {
    buyPosition: buy,
    sellPosition: sell,
    maxProfit,
    maxLoss,
  };
}

function calculatePutBreakEven(positions: Leg[]) {
  const validPositions = positions
    .filter((position) => !position.call)
    .sort((a, b) => b.strikePrice - a.strikePrice);

  if (validPositions.length === 0) return;
  const startStrike = validPositions[0].strikePrice;

  //Use divide and conquer algorithm for better efficiency
  let increment = getSmartIncrement(startStrike);
  let initialProfit = calculateProfit(positions, startStrike);
  if (initialProfit === 0) return startStrike;
  const isShortCall = initialProfit > 0;

  let rangeStart = startStrike;
  let count = 0;
  //find max range
  let expectedProfit = 0;
  while (count < 10) {
    expectedProfit = calculateProfit(positions, rangeStart - increment);
    if (expectedProfit === 0) return rangeStart - increment;
    else if (isShortCall ? expectedProfit > 0 : expectedProfit < 0) {
      rangeStart -= increment;
      increment -= increment;
      count++;
    } else {
      break;
    }
  }
  if (count >= 10) return;
  const roundingPlaces = increment > 1 ? 2 : increment.toString(10).length + 4;
  if (!isShortCall)
    return findShortRange(
      positions,
      rangeStart - increment,
      rangeStart,
      roundingPlaces
    );
  else
    return findLongRange(
      positions,
      rangeStart - increment,
      rangeStart,
      roundingPlaces
    );

}

function calculateCallBreakEven(positions: Leg[]) {
  const validPositions = positions
    .filter((position) => position.call)
    .sort((a, b) => a.strikePrice - b.strikePrice);

  if (validPositions.length === 0) return;

  const startStrike = validPositions[0].strikePrice;

  //Use divide and conquer algorithm for better efficiency
  let increment = getSmartIncrement(startStrike);
  let initialProfit = calculateProfit(positions, startStrike);
  if (initialProfit === 0) return startStrike;
  const isShortCall = initialProfit > 0;

  let rangeStart = startStrike;
  let count = 0;
  //find max range
  let expectedProfit = 0;
  while (count < 10) {
    expectedProfit = calculateProfit(positions, rangeStart + increment);
    if (expectedProfit === 0) return rangeStart + increment;
    else if (isShortCall ? expectedProfit > 0 : expectedProfit < 0) {
      rangeStart += increment;
      increment += increment;
      count++;
    } else {
      break;
    }
  }
  if (count >= 10) return;
  const roundingPlaces = increment > 1 ? 2 : increment.toString(10).length + 4;

  if (isShortCall)
    return findShortRange(
      positions,
      rangeStart,
      rangeStart + increment,
      roundingPlaces
    );
  else
    return findLongRange(
      positions,
      rangeStart,
      rangeStart + increment,
      roundingPlaces
    );
}

function findShortRange(
  positions: Leg[],
  min: number,
  max: number,
  roundingPlaces: number = 2
): any {
  const mid = parseFloat(((min + max) / 2).toFixed(roundingPlaces));
  const expectedProfit = calculateProfit(positions, mid);
  if (Math.abs(min - mid) < Math.pow(10, -roundingPlaces)) return mid; //cannot find breakeven after many recursions
  if (Math.abs(max - mid) < Math.pow(10, -roundingPlaces)) return mid; //cannot find breakeven after many recursions
  if (expectedProfit === 0) return mid;
  else if (expectedProfit > 0) {
    return findShortRange(positions, mid, max, roundingPlaces);
  } else return findShortRange(positions, min, mid, roundingPlaces);
}

function findLongRange(
  positions: Leg[],
  min: number,
  max: number,
  roundingPlaces: number = 2
): any {
  const mid = parseFloat(((min + max) / 2).toFixed(roundingPlaces));
  const expectedProfit = calculateProfit(positions, mid);
  if (Math.abs(min - mid) < Math.pow(10, -roundingPlaces)) return mid; //cannot find breakeven after many recursions
  if (Math.abs(max - mid) < Math.pow(10, -roundingPlaces)) return mid; //cannot find breakeven after many recursions
  if (expectedProfit === 0) return mid;
  else if (expectedProfit < 0) {
    return findLongRange(positions, mid, max, roundingPlaces);
  } else return findLongRange(positions, min, mid, roundingPlaces);
}

function getSmartIncrement(startStrike: number) {
  if (startStrike > 10) {
    return Math.pow(10, Math.round(startStrike).toString(10).length - 1);
  } else if (startStrike > 1) {
    return 1;
  } else {
    let count = 1;
    do {
      startStrike *= 10;
      count++;
    } while (startStrike < 1);
    return Math.pow(10, -count);
  }
}

function calculateBreakEven(positions: Leg[]) {
  return {
    callBreakEven: calculateCallBreakEven(positions),
    putBreakEven: calculatePutBreakEven(positions),
  };
}

export function analyzePositions(positions: Leg[]) {
  let maxProfit = 0,
    maxLoss = 0;
  const pairPositions: PairPosition[] = [];
  const callBuyPositions: Leg[] = [];
  const callSellPositions: Leg[] = [];
  const putBuyPositions: Leg[] = [];
  const putSellPositions: Leg[] = [];
  positions.forEach((position: Leg) => {
    let oppositePosition;
    if (position.buy) {
      if (position.call) { //Buying call options
        oppositePosition = callSellPositions.find(
          (value) => value.quantity === -position.quantity
        );
        if (oppositePosition) {
          pairPositions.push(createPairPosition(position, oppositePosition));
          callSellPositions.pop();
        } else {
          callBuyPositions.push(position);
        }
      } else { //Buying put options
        oppositePosition = putSellPositions.find(
          (value) => value.quantity === -position.quantity
        );
        if (oppositePosition) {
          pairPositions.push(createPairPosition(position, oppositePosition));
          putSellPositions.pop();
        } else {
          putBuyPositions.push(position);
        }
      }
    } else {
      if (position.call) { //Selling call options
        oppositePosition = callBuyPositions.find(
          (value) => value.quantity === -position.quantity
        );
        if (oppositePosition) {
          pairPositions.push(createPairPosition(oppositePosition, position));
          callBuyPositions.pop();
        } else {
          callSellPositions.push(position);
        }
      } else { //Selling put options
        oppositePosition = putBuyPositions.find(
          (value) => value.quantity === -position.quantity
        );
        if (oppositePosition) {
          pairPositions.push(createPairPosition(oppositePosition, position));
          putBuyPositions.pop();
        } else {
          putSellPositions.push(position);
        }
      }
    }
  });

  for (let i = 0; i < pairPositions.length; i++) {
    const pairPosition = pairPositions[i];
    maxProfit += pairPosition.maxProfit;
    maxLoss += pairPosition.maxLoss;
  }

  if (callBuyPositions.length > 0 || putBuyPositions.length > 0) {
    maxProfit = Infinity;
    if (maxLoss !== Infinity) {
      [...callBuyPositions, ...putBuyPositions].forEach((value) => {
        maxLoss += createPairPosition(value).maxLoss;
      });
    }
  }

  if (callSellPositions.length > 0 || putSellPositions.length > 0) {
    maxLoss = Infinity;
    if (maxProfit !== Infinity) {
      [...callSellPositions, ...putSellPositions].forEach((value) => {
        maxProfit += createPairPosition(undefined, value).maxProfit;
      });
    }
  }

  return {
    maxProfit: Math.abs(maxProfit),
    maxLoss: Math.abs(maxLoss),
    breakEven: calculateBreakEven(positions),
  };
}
